import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Sparkles, Sofa, SlidersHorizontal, Check } from "lucide-react";

export const VerdictBadge = ({ verdict, className, testid }) => {
  const v = verdict || "none";
  const label = { pass: "Godkjent", needs_review: "Til vurdering", reject: "Avvist", none: "Ikke vurdert" }[v] || v;
  return <span data-testid={testid || "verdict-badge"} className={cn("inline-flex items-center rounded-[8px] border px-2.5 py-0.5 text-[11.5px] font-medium", `verdict-${v}`, className)}>{label}</span>;
};

const STATUS_LABEL = { queued: "I kø", processing: "Kjører", reviewing: "Kontrolleres", completed: "Ferdig", failed: "Feilet" };

export const StatusBadge = ({ status, className, testid }) => {
  const s = status || "queued";
  return (
    <span data-testid={testid || "job-status-badge"} className={cn("inline-flex items-center gap-1.5 rounded-[8px] px-2.5 py-1 text-[12px] font-medium", `status-${s}`, className)}>
      {(s === "processing" || s === "reviewing" || s === "queued") ? <span className="h-1.5 w-1.5 rounded-full bg-current pulse-soft" /> : s === "completed" ? <Check className="h-3 w-3" /> : null}
      {STATUS_LABEL[s] || s}
    </span>
  );
};

/** The three image treatments, always visually distinct: real-photo optimisation, soft styling, virtual furnishing. */
export const TREATMENT = {
  optimize_only: { label: "Optimalisert", cls: "badge-opt", Icon: SlidersHorizontal, testid: "badge-optimized" },
  soft_style: { label: "Digitalt stylet", cls: "badge-style", Icon: Sparkles, testid: "badge-styled" },
  virtual_stage: { label: "Virtuelt møblert", cls: "badge-furn", Icon: Sofa, testid: "badge-furnished" },
};

export const TreatmentBadge = ({ kind, className, size = "sm", withIcon = true, testid }) => {
  const t = TREATMENT[kind];
  if (!t) return null;
  return (
    <span data-testid={testid || t.testid} className={cn("inline-flex items-center gap-1 rounded-[8px] font-medium", t.cls, size === "xs" ? "px-1.5 py-0.5 text-[10.5px]" : "px-2.5 py-1 text-[11.5px]", className)}>
      {withIcon && <t.Icon className={size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5"} />}{t.label}
    </span>
  );
};

export const ProvenanceBadge = ({ source, className }) => {
  const map = {
    digihome_property_data: { label: "Eiendomsdata", cls: "bg-secondary text-foreground border-transparent" },
    image_analysis: { label: "Bildeanalyse", cls: "surface-info border-transparent" },
    user_input: { label: "Fra eier", cls: "bg-secondary text-foreground border-transparent" },
    source_listing: { label: "Fra annonsen", cls: "surface-accent border-transparent" },
  };
  const m = map[source] || { label: source || "ukjent", cls: "bg-muted text-muted-foreground" };
  return <span data-testid="provenance-badge" className={cn("inline-flex items-center rounded-[6px] border px-2 py-0.5 text-[10.5px] font-medium", m.cls, className)}>{m.label}</span>;
};

export const MetricChip = ({ label, value, tone = "neutral", hint, testid, mono = true }) => {
  const toneCls = {
    neutral: "bg-secondary text-foreground",
    good: "verdict-pass border",
    warn: "verdict-needs_review border",
    bad: "verdict-reject border",
  }[tone];
  const chip = (
    <span data-testid={testid} className={cn("inline-flex items-center gap-1.5 rounded-[8px] px-2.5 py-1 text-xs", toneCls)}>
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-medium metric-num", mono && "font-mono")}>{value}</span>
    </span>
  );
  if (!hint) return chip;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs">{hint}</TooltipContent>
    </Tooltip>
  );
};

export const scoreTone = (v, good = 0.75, warn = 0.5) => (v == null ? "neutral" : v >= good ? "good" : v >= warn ? "warn" : "bad");

/** Soft pastel status card ("TILKOBLET" pattern): round icon, eyebrow, bold title, muted body. */
export const StatusCard = ({ tone = "success", eyebrow, title, children, icon: Icon = Check, actions, className, testid }) => (
  <div className={cn("rounded-[20px] border p-5 sm:p-6", `surface-${tone}`, className)} data-testid={testid}>
    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
      <div className="h-10 w-10 shrink-0 rounded-full bg-white/90 dark:bg-white/10 flex items-center justify-center shadow-sm"><Icon className="h-5 w-5" /></div>
      <div className="min-w-0 flex-1 space-y-1">
        {eyebrow && <div className="eyebrow" style={{ color: "inherit", opacity: 0.75 }}>{eyebrow}</div>}
        {title && <h2 className="font-heading text-[22px] leading-snug tracking-[-0.02em]">{title}</h2>}
        {children && <div className="text-sm opacity-90 leading-relaxed">{children}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 sm:pl-2">{actions}</div>}
    </div>
  </div>
);
